/**
 * CompactCardLayout - Shared compact display for CRM entity cards.
 *
 * Shows avatar (image or entity-type icon fallback), display name,
 * and an entity type pill badge. Used by ContactCardContent,
 * CompanyCardContent, and DealCardContent when `compact` is true.
 *
 * Internal to object-cards — not exported from the public barrel.
 */

import { useCallback } from 'react';

import { Avatar } from '@/lib/base/avatar/avatar';
import { Badge } from '@/lib/base/badges/badges';
import { Skeleton } from '@/lib/base/skeleton/skeleton';
import { getTypeLabelByName } from '@/lib/crm/utils/getTypeLabel';
import { getEntityTypeIconComponent } from '@/lib/crm/utils/icons/EntityTypeIcon';
import { cx } from '@/utils/cx';

export interface CompactCardLayoutProps {
  /** Display name */
  name: string;
  /** Entity type name (e.g. "ahoy_contact") — used for icon fallback and type label */
  entityTypeName: string;
  /** Avatar image URL */
  avatarUrl?: string;
  /** Initials fallback for avatar */
  initials?: string;
  /**
   * Forwarded to the avatar image's `onLoad`. Lets a caller run the same
   * favicon-quality guard it uses in full mode (e.g. CompanyCardContent's
   * "16×16 default globe → fall back to initials") so a compact card never
   * shows a worse avatar than the full card would.
   */
  onAvatarLoad?: (e: React.SyntheticEvent<HTMLImageElement>) => void;
  /**
   * When provided, the name becomes a clickable button that invokes this
   * callback — used by proposal action cards to open the CRM slideout for
   * the underlying entity. The avatar and type badge remain non-interactive.
   */
  onClick?: () => void;
  /**
   * When true, the name renders as a text skeleton instead of the string.
   * The entity-type badge still renders because its value is known from
   * `entityTypeName` (sync, not store-dependent).
   */
  isLoading?: boolean;
  /**
   * Omit the entity-type pill. Use when the surrounding context already
   * names the type — e.g. an association chip sitting under a "Company"
   * relationship label — so the type isn't announced twice.
   */
  hideTypeBadge?: boolean;
  className?: string;
}

export function CompactCardLayout({
  name,
  entityTypeName,
  avatarUrl,
  initials,
  onAvatarLoad,
  onClick,
  isLoading = false,
  hideTypeBadge = false,
  className,
}: CompactCardLayoutProps) {
  const IconComponent = getEntityTypeIconComponent(entityTypeName);
  const typeLabel = getTypeLabelByName(entityTypeName);

  // Native <button> handles Enter / Space activation semantics correctly,
  // including debouncing on auto-repeat. A previous implementation used a
  // <p role="button"> with an onKeyDown handler, which fired onClick once
  // per keyrepeat tick and could open the slideout multiple times.
  const handleNameClick = useCallback(
    (e: React.MouseEvent) => {
      if (onClick) {
        e.stopPropagation();
        onClick();
      }
    },
    [onClick]
  );

  // The name only needs bottom spacing when a badge sits beneath it.
  const nameSpacing = hideTypeBadge ? undefined : 'mb-1.5';

  let nameNode;
  if (isLoading) {
    nameNode = (
      <div className={nameSpacing}>
        <Skeleton variant='text' width={100} height={14} />
      </div>
    );
  } else if (onClick) {
    nameNode = (
      <button
        type='button'
        onClick={handleNameClick}
        className={cx(
          'text-sm font-medium text-fg-primary truncate block max-w-full text-left',
          nameSpacing,
          'cursor-pointer hover:underline hover:text-fg-brand-primary',
          'bg-transparent border-0 p-0'
        )}
      >
        {name}
      </button>
    );
  } else {
    nameNode = (
      <p
        className={cx(
          'text-sm font-medium text-fg-primary truncate',
          nameSpacing
        )}
      >
        {name}
      </p>
    );
  }

  return (
    <div className={cx('flex items-center gap-3', className)}>
      <Avatar
        size='sm'
        src={avatarUrl}
        initials={initials}
        placeholderIcon={IconComponent}
        onLoad={avatarUrl ? onAvatarLoad : undefined}
      />
      <div className='min-w-0 '>
        {nameNode}
        {!hideTypeBadge && (
          <Badge size='sm' color='brand'>
            {typeLabel}
          </Badge>
        )}
      </div>
    </div>
  );
}
